/**
 * Presentation every trick-taking game says the same way.
 *
 * A GameUi is still the one that decides what its game says; these are the
 * sentences that come out identical in more than one of them — who is winning
 * the trick, the line under a seat, the order a hand is fanned in — so Pitch,
 * Belote and Bridge compose them rather than each keeping a copy. 500 predates
 * this file and keeps its own, which reads its bowers and Joker.
 */
import type { ReactNode } from 'react'
import type { CardId, PendingDecision, RankingContext, Seat, Suit } from '@cards/shared'
import type { AuctionSeatCall, DecisionBodyProps } from './types'
import type { TableView } from '../table/view'
import { Glyph } from '../table/atoms'
import { cardText } from '../cards/assets'

/** `winner` once the trick is settled, `winning` while it is still on the
 *  table. Both come from the server; the client has no second opinion. */
export function winningSeat(view: TableView): Seat | null {
  const trick = view.trick
  if (!trick || trick.plays.length === 0) return null
  return trick.winner ?? trick.winning ?? null
}

/** `partner, bot, winning` — plus whatever the game adds (`pitcher`). */
export function seatStatus(view: TableView, seat: Seat, extra: readonly ReactNode[] = []): ReactNode | null {
  const s = view.seats[seat]
  if (!s) return null
  if (!s.active) return 'sits out'
  const parts: ReactNode[] = []
  if (s.isPartner) parts.push('partner')
  // A bot is the laptop ahead of its name (Felt.tsx), not a word down here.
  if (!s.connected && !s.isBot) parts.push('away')
  parts.push(...extra)

  const trick = view.trick
  if (trick) {
    if (trick.winner === seat) parts.push('takes the trick')
    else if (trick.winner === null && trick.plays.length > 0) {
      if (trick.winning === seat) parts.push('winning')
      else if (trick.leader === seat && trick.ledSuit) {
        parts.push(
          <span key="led">
            led <Glyph suit={trick.ledSuit} />
          </span>,
        )
      }
    } else if (trick.plays.length === 0 && view.previousTrick?.winner === seat) {
      parts.push('leads next')
    }
  }
  if (parts.length === 0) return null
  return (
    <>
      {parts.map((p, i) => (
        <span key={i}>
          {i > 0 ? ', ' : ''}
          {p}
        </span>
      ))}
    </>
  )
}

/** `Your turn, follow ♠` / `Mia to play`. `followWord` lets a game say what
 *  following means where it is not only the suit led (Pitch: `or trump`). */
export function handHint(view: TableView, opts: { lead?: ReactNode; followTail?: ReactNode } = {}): ReactNode {
  const trick = view.trick
  if (view.isMyTurn) {
    if (trick && trick.plays.length > 0 && trick.ledSuit && view.legalPlays.size < view.myCards.length) {
      return (
        <>
          <b>Your turn,</b> follow <Glyph suit={trick.ledSuit} />
          {opts.followTail ?? null}
        </>
      )
    }
    if (!trick || trick.plays.length === 0) {
      return opts.lead ?? (
        <>
          <b>Your turn,</b> lead
        </>
      )
    }
    return <b>Your turn</b>
  }
  if (trick && trick.winner !== null) return `${view.name(trick.winner)} leads next`
  if (view.toAct !== null) return `${view.name(view.toAct)} to play`
  return 'Your hand'
}

/** `10♠` with the glyph coloured, for sentences on the felt. */
export function CardWord({ card }: { card: CardId }): JSX.Element {
  if (card === 'JOKER') return <span>the Joker</span>
  const text = cardText(card)
  const suit = text.slice(-1)
  const glyph: Record<string, Suit> = { '♠': 'S', '♥': 'H', '♦': 'D', '♣': 'C' }
  const s = glyph[suit]
  return (
    <span>
      {text.slice(0, -1)}
      {s ? <Glyph suit={s} /> : suit}
    </span>
  )
}

const SUIT_ORDER: readonly Suit[] = ['S', 'H', 'C', 'D']
const RED: ReadonlySet<Suit> = new Set<Suit>(['H', 'D'])

/** Plain suits alternating colour where the pack allows, trumps last, each
 *  group low to high — the hand a right-hander arranges, with the trump block
 *  under the thumb. The reasoning is in games/five-hundred/hand.ts. */
export function sortBySuit(cards: readonly CardId[], ctx: RankingContext): CardId[] {
  const left = SUIT_ORDER.filter((suit) => suit !== ctx.trumpSuit)
  const order: Suit[] = []
  while (left.length > 0) {
    const last = order[order.length - 1]
    const at = last ? left.findIndex((suit) => RED.has(suit) !== RED.has(last)) : 0
    order.push(...left.splice(at === -1 ? 0 : at, 1))
  }
  const group = (card: CardId): number => {
    if (ctx.isTrump(card)) return order.length + 1
    const suit = ctx.effectiveSuit(card)
    return suit ? order.indexOf(suit) : order.length
  }
  return [...cards].sort((a, b) => group(a) - group(b) || ctx.strength(a) - ctx.strength(b))
}

/** Each seat's last call from an auction log of `{ seat, call }`, with the
 *  standing bid marked. */
export function auctionStripOf(
  view: TableView,
  calls: readonly { seat: Seat; call: unknown }[],
  label: (call: unknown) => { label: ReactNode; pass: boolean },
  highSeat: Seat | null,
): (AuctionSeatCall | null)[] {
  const out: (AuctionSeatCall | null)[] = new Array(view.seatCount).fill(null)
  for (const { seat, call } of calls) out[seat] = label(call)
  const marked = highSeat === null ? null : out[highSeat]
  if (highSeat !== null && marked && !marked.pass) out[highSeat] = { ...marked, high: true }
  return out
}

/** Every call each seat has made, oldest first, with the standing bid (the
 *  high seat's last call) marked — the history the bid tiles lay out. */
export function auctionCallsOf(
  view: TableView,
  calls: readonly { seat: Seat; call: unknown }[],
  label: (call: unknown) => { label: ReactNode; pass: boolean },
  highSeat: Seat | null,
): AuctionSeatCall[][] {
  const out: AuctionSeatCall[][] = Array.from({ length: view.seatCount }, () => [])
  for (const { seat, call } of calls) out[seat]?.push(label(call))
  const mine = highSeat === null ? undefined : out[highSeat]
  const last = mine?.[mine.length - 1]
  if (mine && last && !last.pass) mine[mine.length - 1] = { ...last, high: true }
  return out
}

/** The auction log as the core's modules all keep it: `calls: {seat, call}[]`. */
export function readCalls(auction: unknown): { seat: Seat; call: unknown }[] {
  const calls = (auction as { calls?: unknown } | null)?.calls
  if (!Array.isArray(calls)) return []
  return calls.filter(
    (c): c is { seat: Seat; call: unknown } =>
      typeof c === 'object' && c !== null && typeof (c as { seat?: unknown }).seat === 'number',
  )
}

interface DecisionOption {
  readonly label: string
  readonly decision: unknown
}

/** Option prompts are validated by membership, exactly like calls, so the
 *  chosen option is submitted back verbatim. */
export function optionsOf(pending: PendingDecision): DecisionOption[] {
  const payload = pending.payload as Record<string, unknown> | null
  const raw = Array.isArray(payload?.options) ? (payload!.options as unknown[]) : []
  return raw.map((option) => {
    if (typeof option === 'string') return { label: option, decision: option }
    const rec = (option ?? {}) as Record<string, unknown>
    return { label: String(rec.label ?? rec.kind ?? 'None'), decision: option }
  })
}

/** A choice between labelled options, for any prompt a game has not drawn. */
export function OptionsDecision({ pending, onSubmit, busy, title }: DecisionBodyProps & { title?: string }): JSX.Element {
  const options = optionsOf(pending)
  const payload = pending.payload as { label?: unknown } | null
  return (
    <div className="sheet-body">
      <h2>{title ?? (typeof payload?.label === 'string' ? payload.label : pending.kind)}</h2>
      <div className="option-row">
        {options.map((option, i) => (
          <button key={i} className="btn btn-secondary" disabled={busy} onClick={() => onSubmit(option.decision)}>
            {option.label}
          </button>
        ))}
      </div>
    </div>
  )
}
